import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { userWorkspacesQueryOptions } from "@/hooks/use-user-data-viewer";
import { WorkspaceList } from "@/components/user-data-viewer/workspace-list";
import { WorkspaceExplorer } from "@/components/user-data-viewer/workspace-explorer";
import type { ViewerWorkspaceItem } from "@/types/user-data-viewer";

function errorMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message.toLowerCase() : "";
  if (raw.includes("not found")) {
    return "해당 이메일/ID의 유저를 찾을 수 없습니다.";
  }
  return "유저 데이터를 불러오지 못했습니다.";
}

export default function UserDataViewerPage() {
  const [input, setInput] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] =
    useState<ViewerWorkspaceItem | null>(null);

  const query = useQuery(
    useMemo(() => userWorkspacesQueryOptions(submitted), [submitted])
  );

  const handleSearch = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setSelectedWorkspace(null);
    setSubmitted(trimmed);
  }, [input]);

  if (selectedWorkspace) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedWorkspace(null)}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            워크스페이스 목록
          </Button>
          <div className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
            <span className="truncate">{query.data?.user.email}</span>
            <span>/</span>
            <span className="truncate font-medium text-foreground">
              {selectedWorkspace.name || "워크스페이스"}
            </span>
          </div>
        </div>
        <WorkspaceExplorer
          workspaceId={selectedWorkspace.id}
          workspaceName={selectedWorkspace.name}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          유저 데이터 뷰어
        </h1>
        <p className="text-muted-foreground leading-normal">
          유저 이메일 또는 ID로 워크스페이스를 조회하고, 폴더·문서를 열어 본문을
          읽습니다.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">유저 조회</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="viewer-email-or-id">이메일 또는 유저 ID</Label>
            <div className="flex gap-2">
              <Input
                id="viewer-email-or-id"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="user@example.com 또는 users 컬렉션의 _id"
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSearch();
                }}
              />
              <Button onClick={handleSearch} disabled={!input.trim()}>
                <Search className="mr-2 h-4 w-4" />
                조회
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {submitted && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">워크스페이스</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {query.isLoading && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            )}

            {query.isError && (
              <div className="rounded border p-4 text-sm text-destructive">
                {errorMessage(query.error)}
              </div>
            )}

            {query.data && (
              <>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border bg-muted/30 p-3 text-sm">
                  <span className="font-medium">{query.data.user.email}</span>
                  {query.data.user.name && (
                    <span className="text-muted-foreground">
                      {query.data.user.name}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    워크스페이스 {query.data.workspaces.length.toLocaleString()}개
                  </span>
                </div>

                <WorkspaceList
                  workspaces={query.data.workspaces}
                  onSelect={setSelectedWorkspace}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
